import React from "react";
import { motion } from "framer-motion";
import { Zap, Lightbulb, IndianRupee, ShieldCheck } from "lucide-react";

const items = [
    {
        id: "fast-delivery",
        icon: Zap,
        title: "Fast Delivery",
        desc: "Tight, predictable turnarounds without cutting creative corners.",
    },
    {
        id: "creative-concepts",
        icon: Lightbulb,
        title: "Creative Concepts",
        desc: "Strategy‑led ideas that earn attention — not just look pretty.",
    },
    {
        id: "affordable-pricing",
        icon: IndianRupee,
        title: "Affordable Pricing",
        desc: "Studio quality at sensible rates built for growing businesses.",
    },
    {
        id: "professional-execution",
        icon: ShieldCheck,
        title: "Professional Execution",
        desc: "Detail‑obsessed delivery — print, digital, and on‑location.",
    },
];

export const WhyChooseUs = () => {
    return (
        <section
            data-testid="why-choose-us-section"
            className="relative py-24 sm:py-32 bg-[#080808] border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6 }}
                    className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14"
                >
                    <div className="lg:col-span-5">
                        <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-4">
                            Why Choose Us
                        </div>
                        <h2 className="font-poppins font-semibold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
                            Built to make your brand <span className="gold-gradient-text">unmistakable</span>.
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 self-end">
                        <p className="font-inter text-base leading-relaxed text-zinc-400">
                            We're a small, senior team obsessed with craft. No middlemen, no juniors — every
                            project is led, designed, and delivered by people who've been doing this for years.
                        </p>
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                    {items.map((it, i) => {
                        const Icon = it.icon;
                        return (
                            <motion.div
                                key={it.id}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.5, delay: i * 0.07 }}
                                data-testid={`why-card-${it.id}`}
                                className="group relative bg-[#0c0c0c] border border-white/5 rounded-2xl p-7 hover:border-gold/30 transition-colors"
                            >
                                <div className="font-poppins text-xs text-zinc-600 mb-6">
                                    0{i + 1}
                                </div>
                                <div className="w-11 h-11 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center mb-5">
                                    <Icon size={18} className="text-gold" />
                                </div>
                                <h3 className="font-poppins font-medium text-white text-lg">
                                    {it.title}
                                </h3>
                                <p className="mt-2 font-inter text-sm text-zinc-400 leading-relaxed">
                                    {it.desc}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default WhyChooseUs;
